import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Shield, UserPlus, Edit2, Trash2, Loader2, AlertCircle, X, Check } from "lucide-react";
import {
  fetchModerators,
  fetchAdminUsers,
  promoteToModerator,
  updateModeratorPermissions,
  revokeModeratorRole,
} from "@/store/slices/adminSlice";
import { toast } from "react-toastify";

const AVAILABLE_PERMISSIONS = [
  { key: "route_management", label: "Route Management", desc: "View, approve, reject, hide/unhide routes" },
  { key: "user_management", label: "User Management", desc: "View users, suspend/unsuspend (not admins)" },
  { key: "sponsor_management", label: "Sponsor Management", desc: "View sponsors and sponsorships (read-only)" },
  { key: "organizer_management", label: "Organizer Management", desc: "View organizer applications (read-only)" },
];

const ModeratorsPage = () => {
  const dispatch = useDispatch();
  const { moderators, isLoadingModerators, moderatorsError, isUpdating, users } = useSelector(
    (s) => s.admin
  );

  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(null); // moderator obj
  const [searchQuery, setSearchQuery] = useState("");
  const [promoteUserId, setPromoteUserId] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);

  useEffect(() => {
    dispatch(fetchModerators());
  }, [dispatch]);

  // Fetch users for promote modal search
  useEffect(() => {
    if (showPromoteModal) {
      dispatch(fetchAdminUsers({ limit: 100, role: "user" }));
    }
  }, [showPromoteModal, dispatch]);

  const togglePermission = (key) => {
    setSelectedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handlePromote = async () => {
    if (!promoteUserId || selectedPermissions.length === 0) {
      toast.error("Select a user and at least one permission");
      return;
    }
    const result = await dispatch(
      promoteToModerator({ userId: promoteUserId, permissions: selectedPermissions })
    );
    if (!result.error) {
      toast.success("User promoted to moderator");
      setShowPromoteModal(false);
      setPromoteUserId("");
      setSelectedPermissions([]);
      dispatch(fetchModerators());
    } else {
      toast.error(result.payload?.message || "Failed to promote user");
    }
  };

  const handleUpdatePermissions = async () => {
    if (!showEditModal || selectedPermissions.length === 0) return;
    const result = await dispatch(
      updateModeratorPermissions({ userId: showEditModal._id, permissions: selectedPermissions })
    );
    if (!result.error) {
      toast.success("Permissions updated");
      setShowEditModal(null);
    } else {
      toast.error(result.payload?.message || "Failed to update permissions");
    }
  };

  const handleRevoke = async (userId, username) => {
    if (!window.confirm(`Revoke moderator role from ${username}? They will become a regular user.`)) return;
    const result = await dispatch(revokeModeratorRole(userId));
    if (!result.error) {
      toast.success("Moderator role revoked");
    } else {
      toast.error(result.payload?.message || "Failed to revoke role");
    }
  };

  const filteredUsers = (users || []).filter(
    (u) =>
      u.role === "user" &&
      (u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="p-6 max-w-5xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Shield className="w-7 h-7 text-green-600" />
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Moderators</h1>
          <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-sm font-medium">
            {moderators.length}
          </span>
        </div>
        <button
          onClick={() => {
            setShowPromoteModal(true);
            setSelectedPermissions([]);
            setPromoteUserId("");
            setSearchQuery("");
          }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors font-medium"
        >
          <UserPlus className="w-4 h-4" />
          Promote User
        </button>
      </div>

      {/* Error */}
      {moderatorsError && (
        <div className="mb-4 flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 text-red-600 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {moderatorsError}
        </div>
      )}

      {/* Loading */}
      {isLoadingModerators ? (
        <div className="flex justify-center py-16">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        </div>
      ) : moderators.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" />
          <p className="text-lg font-medium">No moderators yet</p>
          <p className="text-sm mt-1">Promote a user to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {moderators.map((mod) => (
            <div
              key={mod._id}
              className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-gray-800 dark:text-gray-100">{mod.name}</span>
                  <span className="text-sm text-gray-500 dark:text-gray-400">@{mod.username}</span>
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{mod.email}</div>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {mod.moderator_permissions?.map((perm) => (
                    <span
                      key={perm}
                      className="inline-flex px-2 py-0.5 text-xs font-medium bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-full"
                    >
                      {perm.replace("_", " ")}
                    </span>
                  ))}
                </div>
                {mod.promoted_by && (
                  <div className="text-xs text-gray-400 mt-1">
                    Promoted by {mod.promoted_by.name || mod.promoted_by.username}
                    {mod.promoted_at && ` • ${new Date(mod.promoted_at).toLocaleDateString()}`}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={() => {
                    setShowEditModal(mod);
                    setSelectedPermissions([...(mod.moderator_permissions || [])]);
                  }}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                  title="Edit permissions"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleRevoke(mod._id, mod.username)}
                  className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                  title="Revoke moderator role"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ======= PROMOTE MODAL ======= */}
      {showPromoteModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">Promote to Moderator</h2>
              <button onClick={() => setShowPromoteModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* User search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Select User</label>
                <input
                  type="text"
                  placeholder="Search by name, username, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-green-500 outline-none"
                />
                {searchQuery.length >= 2 && (
                  <div className="mt-2 max-h-36 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg">
                    {filteredUsers.length === 0 ? (
                      <div className="p-3 text-sm text-gray-500 text-center">No matching users</div>
                    ) : (
                      filteredUsers.slice(0, 10).map((u) => (
                        <button
                          key={u._id}
                          onClick={() => {
                            setPromoteUserId(u._id);
                            setSearchQuery(u.username);
                          }}
                          className={`w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm border-b border-gray-100 dark:border-gray-700 last:border-0 transition-colors ${
                            promoteUserId === u._id ? "bg-green-50 dark:bg-green-900/20" : ""
                          }`}
                        >
                          <span className="font-medium">{u.name}</span>{" "}
                          <span className="text-gray-500">@{u.username}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* Permissions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Permissions</label>
                <div className="space-y-2">
                  {AVAILABLE_PERMISSIONS.map((p) => (
                    <label
                      key={p.key}
                      className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                        selectedPermissions.includes(p.key)
                          ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                          : "border-gray-200 dark:border-gray-600 hover:border-gray-300"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(p.key)}
                        onChange={() => togglePermission(p.key)}
                        className="mt-0.5 accent-green-600"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{p.label}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowPromoteModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handlePromote}
                disabled={!promoteUserId || selectedPermissions.length === 0 || isUpdating}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Promote
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ======= EDIT PERMISSIONS MODAL ======= */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                Edit Permissions — @{showEditModal.username}
              </h2>
              <button onClick={() => setShowEditModal(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-2">
              {AVAILABLE_PERMISSIONS.map((p) => (
                <label
                  key={p.key}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                    selectedPermissions.includes(p.key)
                      ? "border-green-500 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-200 dark:border-gray-600 hover:border-gray-300"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPermissions.includes(p.key)}
                    onChange={() => togglePermission(p.key)}
                    className="mt-0.5 accent-green-600"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{p.label}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{p.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowEditModal(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdatePermissions}
                disabled={selectedPermissions.length === 0 || isUpdating}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorsPage;
